import { CommonModule } from '@angular/common';
import { Component, forwardRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormsModule, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator } from '@angular/forms';
import { Subscription } from 'rxjs';
import { LegalEntity, legalEntityId } from '../models/legal-entity.model';
import { SettlementsService } from '../services/settlements.service';

@Component({
  selector: 'app-legal-entity-select',
  standalone: true,
  imports: [CommonModule, FormsModule],
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => LegalEntitySelectComponent), multi: true },
    { provide: NG_VALIDATORS, useExisting: forwardRef(() => LegalEntitySelectComponent), multi: true }
  ],
  template: `
    <label [for]="inputId" class="form-label ms-2">{{ label }}<span *ngIf="required" aria-hidden="true"> *</span></label>
    <select [id]="inputId" class="form-select" [ngModel]="value" [ngModelOptions]="{ standalone: true }"
      (ngModelChange)="select($event)" (blur)="onTouched()" [disabled]="disabled || loading || !!error"
      [attr.aria-required]="required" [attr.aria-describedby]="error ? inputId + '-error' : null">
      <option [ngValue]="null">{{ loading ? 'Cargando razones sociales…' : (required ? 'Selecciona una razón social' : 'Todas') }}</option>
      <option *ngFor="let entity of entities" [ngValue]="entity.id">{{ entity.business_name }} — {{ entity.company }}</option>
    </select>
    <div *ngIf="error" [id]="inputId + '-error'" class="text-danger text-sm mt-1" role="alert">
      {{ error }} <button type="button" class="btn btn-link btn-sm p-0" (click)="loadEntities()" [disabled]="disabled">Reintentar</button>
    </div>
  `
})
export class LegalEntitySelectComponent implements ControlValueAccessor, Validator, OnChanges, OnDestroy, OnInit {
  @Input() isCommercial: boolean | null = null;
  @Input() required = false;
  @Input() label = 'Razón social';
  @Input() inputId = 'legal-entity';
  value: number | null = null;
  entities: LegalEntity[] = [];
  disabled = false;
  loading = false;
  error = '';
  private loaded = false;
  private request?: Subscription;
  private onChange: (value: number | null) => void = () => {};
  onTouched: () => void = () => {};
  private onValidatorChange: () => void = () => {};

  constructor(private store: SettlementsService) {}

  ngOnInit(): void { if (!this.loaded && !this.loading && !this.error) this.loadEntities(); }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isCommercial']) this.loadEntities();
    if (changes['required']) this.onValidatorChange();
  }
  ngOnDestroy(): void { this.request?.unsubscribe(); }
  writeValue(value: unknown): void { this.value = legalEntityId(value); }
  registerOnChange(fn: (value: number | null) => void): void { this.onChange = fn; }
  registerOnTouched(fn: () => void): void { this.onTouched = fn; }
  registerOnValidatorChange(fn: () => void): void { this.onValidatorChange = fn; }
  setDisabledState(disabled: boolean): void { this.disabled = disabled; }

  validate(control: AbstractControl): ValidationErrors | null {
    const id = legalEntityId(control.value);
    if (this.required && id === null) return { required: true };
    if (id === null) return null;
    if (this.loading || this.error || !this.loaded) return { legalEntityUnavailable: true };
    return this.entities.some(entity => entity.id === id) ? null : { legalEntityInvalid: true };
  }

  select(value: unknown): void {
    this.value = legalEntityId(value);
    this.onChange(this.value);
    this.onTouched();
  }

  loadEntities(): void {
    this.request?.unsubscribe();
    this.loading = true;
    this.loaded = false;
    this.error = '';
    this.entities = [];
    this.onValidatorChange();
    this.request = this.store.getLegalEntities(this.isCommercial).subscribe({
      next: entities => {
        this.entities = entities;
        this.loading = false;
        this.loaded = true;
        this.onValidatorChange();
      },
      error: () => {
        this.loading = false;
        this.error = 'No se pudieron cargar las razones sociales.';
        this.onValidatorChange();
      }
    });
  }

}
